import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "../api.ts";
import { Badge, Button, Card, ErrorText, PageHeader, Spinner, Switch } from "../components/ui.tsx";
import { ScanNotes, statusBadge, Viewer } from "./Skills.tsx";

export function MySkillsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["my-skills"], queryFn: () => unwrap(api.me.skills.$get()) });
  const [viewing, setViewing] = useState<string | null>(null);
  const privacy = useMutation({
    mutationFn: (a: { id: string; private: boolean }) => unwrap(api.me.skills[":id"].private.$post({ param: { id: a.id }, json: { private: a.private } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["my-skills"] }),
  });
  const refresh = useMutation({
    mutationFn: () => unwrap(api.me.skills.refresh.$post()),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["my-skills"] }),
  });
  if (q.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (q.error) return <ErrorText error={q.error} />;
  const { mine, company, sharingEnabled, canPublish } = q.data;
  const sharing = sharingEnabled && canPublish;

  return (
    <>
      <PageHeader
        title="Skills"
        description="Skills are step-by-step know-how your assistant saves and reuses. Ask it to “save this as a skill” after it gets something right."
      />

      <div className="mb-1 flex items-center gap-3">
        <h2 className="flex-1 text-base font-semibold">Your skills</h2>
        {refresh.data && !refresh.isPending && <span className="text-xs text-[var(--muted)]">Up to date</span>}
        <Button disabled={refresh.isPending} onClick={() => refresh.mutate()}>{refresh.isPending ? "Checking…" : "Refresh"}</Button>
      </div>
      <p className="mb-3 text-sm text-[var(--muted)]">
        {sharing
          ? "Skills your assistant creates are checked for sensitive data (passwords, personal details, confidential client data) and then shared with your colleagues' assistants. Keep any skill private with the switch."
          : !sharingEnabled
            ? "Skills your assistant creates stay in your assistant — sharing skills with the company is switched off."
            : "Skills your assistant creates stay in your assistant — your group doesn't share skills with the company."}
      </p>
      <ErrorText error={privacy.error ?? refresh.error} />
      <Card className="mb-10 divide-y divide-[var(--line)]">
        {mine.length === 0 && <div className="px-5 py-6 text-sm text-[var(--muted)]">None yet. Ask your assistant to “save this as a skill”, then press Refresh.</div>}
        {mine.map((s) => (
          <div key={s.id} className="flex flex-wrap items-start gap-4 px-5 py-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <button className="font-mono text-sm font-medium hover:underline" onClick={() => setViewing(s.id)}>{s.name}</button>
                <Badge tone={statusBadge[s.status].tone}>{s.status === "blocked" ? "Not shared: sensitive" : s.status === "unshared" ? "Only in your assistant" : statusBadge[s.status].label}</Badge>
                {s.status === "shared" && !s.live && <Badge>A colleague's skill has this name</Badge>}
              </div>
              {s.description && <div className="mt-0.5 text-sm text-[var(--muted)]">{s.description}</div>}
              <ScanNotes scan={s.scan} />
            </div>
            {sharing && s.status !== "hidden" && (
              <div className="w-44">
                <Switch label="Keep private" checked={s.status === "private"} disabled={privacy.isPending} onChange={(v) => privacy.mutate({ id: s.id, private: v })} />
              </div>
            )}
          </div>
        ))}
      </Card>

      <h2 className="mb-1 text-base font-semibold">Company skills</h2>
      <p className="mb-3 text-sm text-[var(--muted)]">Your assistant already has these. Official skills come from your administrator and take precedence.</p>
      <Card className="divide-y divide-[var(--line)]">
        {company.length === 0 && <div className="px-5 py-6 text-sm text-[var(--muted)]">No company skills yet.</div>}
        {company.map((s) => (
          <div key={s.id} className="px-5 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <button className="font-mono text-sm font-medium hover:underline" onClick={() => setViewing(s.id)}>{s.name}</button>
              {s.official ? <Badge tone="accent">Official</Badge> : <span className="text-xs text-[var(--muted)]">by {s.author}</span>}
            </div>
            {s.description && <div className="mt-0.5 text-sm text-[var(--muted)]">{s.description}</div>}
          </div>
        ))}
      </Card>
      {viewing && <Viewer id={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}
